import { cloneElement, isValidElement, useCallback, useEffect, useRef, useState, type MouseEventHandler, type ReactNode } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const MIN_REASON = 8;

/**
 * Minimal, safe reason/case capture for starting a tenant support VIEW session.
 *
 * The reason is required (>= 8 characters) and is passed straight to the
 * server-backed `start_tenant_view_session` RPC, which validates and audits it.
 * We never invent a reason on the caller's behalf.
 *
 * The modal is rendered in place (no portal) as a controlled fixed overlay with
 * role="dialog" + aria-modal, so the whole flow stays in one DOM tree.
 */
export function ViewSessionDialog({
  trigger,
  tenantName,
  onConfirm,
}: {
  trigger: ReactNode;
  tenantName?: string;
  onConfirm: (reason: string, caseRef?: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [caseRef, setCaseRef] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  const valid = reason.trim().length >= MIN_REASON;

  const close = useCallback(() => {
    setOpen(false);
    setReason('');
    setCaseRef('');
    setError(null);
  }, []);

  // Backdrop and Escape only ever dismiss — never confirm — and they stand down
  // while the start RPC is in flight, matching the disabled Cancel button.
  const requestClose = useCallback(() => {
    if (working) return;
    close();
  }, [close, working]);

  useEffect(() => {
    if (!open) return;
    reasonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, requestClose]);

  const handleConfirm = async () => {
    if (!valid) return;
    setWorking(true);
    setError(null);
    try {
      await onConfirm(reason.trim(), caseRef.trim() || undefined);
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start support session');
    } finally {
      setWorking(false);
    }
  };

  // The caller supplies its own trigger element (keeping its label and styling);
  // we only attach the open handler to it.
  const openDialog = () => setOpen(true);
  const triggerNode = isValidElement<{ onClick?: MouseEventHandler }>(trigger)
    ? cloneElement(trigger, { onClick: openDialog })
    : trigger;

  return (
    <>
      {triggerNode}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          // mousedown with an identity check: a drag started inside the panel
          // must not dismiss, and a backdrop press can never reach the controls
          // sitting underneath the overlay.
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) requestClose();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="support-session-title"
            aria-describedby="support-session-description"
            className="relative grid w-full max-w-lg gap-4 rounded-lg border bg-background p-6 shadow-lg"
          >
            <div className="flex flex-col space-y-1.5 text-center sm:text-left">
              <h2 id="support-session-title" className="text-lg font-semibold leading-none tracking-tight">
                Start support session
              </h2>
              <p id="support-session-description" className="text-sm text-muted-foreground">
                {tenantName
                  ? `You are about to view ${tenantName} in read-only support mode.`
                  : 'You are about to view this tenant in read-only support mode.'}{' '}
                This is recorded and audited against your account.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="support-session-reason">Reason (required)</Label>
                <Textarea
                  id="support-session-reason"
                  ref={reasonRef}
                  aria-label="Support session reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Why do you need to view this tenant? (min 8 characters)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support-session-case">Case reference (optional)</Label>
                <Input
                  id="support-session-case"
                  aria-label="Case reference (optional)"
                  value={caseRef}
                  onChange={(event) => setCaseRef(event.target.value)}
                  placeholder="e.g. CASE-123"
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button variant="ghost" onClick={close} disabled={working}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={!valid || working} className="gap-2">
                {working && <Loader2 className="h-4 w-4 animate-spin" />} Start session
              </Button>
            </div>

            <button
              type="button"
              onClick={requestClose}
              disabled={working}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ViewSessionDialog;
